import { TIME_OPTIONS } from "../scheduling";
import CustomSelect from "../../components/CustomSelect";
import { Box } from "@mui/material";
import Flex from "../../components/Flex";
import { useMemo } from "react";

const timeToMinutes = (time: any) => {
  if (!time) return 0;
  const [hours, minutes, seconds] = time.split(":").map(Number);
  return hours * 60 + minutes + seconds / 60;
};

const formatDifference = (startMinutes: any, endMinutes: any) => {
  // Handle wrap-around at midnight
  let diff = endMinutes - startMinutes;
  if (diff <= 0) {
    diff += 24 * 60; // Add 24 hours if end is before start (wraps around midnight)
  }

  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;

  if (diff === 0) {
    return "0 mins";
  }

  const hoursText = hours > 0 ? `${hours} hr${hours > 1 ? "s" : ""}` : "";
  const minutesText = minutes > 0 ? `${minutes} mins` : "";

  return [hoursText, minutesText].filter(Boolean).join(" ").trim();
};

interface Props {
  fullDay?: boolean;
  startTime: string | null;
  endTime: string | null;
  setStartTime: (val: string | null) => void;
  setEndTime: (val: string | null) => void;
  startTimeError?: boolean;
  endTimeError?: boolean;
  required?: boolean;
}

function TimeSlotDropdown({
  fullDay,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  startTimeError,
  endTimeError,
  required,
}: Props) {
  const filteredStartTimeOptions = useMemo(() => {
    if (!endTime) {
      return TIME_OPTIONS;
    }

    const endMinutes = timeToMinutes(endTime);

    // If endTime is 00:00:00 (midnight), allow all times except midnight itself
    // This allows selecting times like 11:45 PM when end is 12:00 AM
    if (endMinutes === 0) {
      return TIME_OPTIONS.filter((option) => timeToMinutes(option.value) !== 0);
    }

    // Otherwise, filter times that are before endTime
    return TIME_OPTIONS.filter(
      (option) => timeToMinutes(option.value) < endMinutes
    );
  }, [endTime]);

  const filteredEndTimeOptions = useMemo(() => {
    if (!startTime) {
      return TIME_OPTIONS;
    }

    const startMinutes = timeToMinutes(startTime);
    const lastTimeMinutes = timeToMinutes(
      TIME_OPTIONS[TIME_OPTIONS.length - 1].value
    );
    const elevenPMMinutes = timeToMinutes("23:00:00"); // 11:00 PM

    // If startTime is the last time (11:45 PM), allow selecting 12:00 AM (midnight)
    if (startMinutes === lastTimeMinutes) {
      return [TIME_OPTIONS[0]].map((option) => ({
        ...option,
        label: `${option.label} (${formatDifference(
          startMinutes,
          timeToMinutes(option.value)
        )})`,
      }));
    }

    // Otherwise, filter times that are after startTime
    // Also allow midnight (00:00:00) if startTime is 11:00 PM or later (for wrap-around)
    return TIME_OPTIONS.filter((option) => {
      const optionMinutes = timeToMinutes(option.value);
      // Allow times after startTime, OR midnight if startTime is 11:00 PM or later
      return (
        optionMinutes > startMinutes ||
        (optionMinutes === 0 && startMinutes >= elevenPMMinutes)
      );
    }).map((option) => ({
      ...option,
      label: `${option.label} (${formatDifference(
        startMinutes,
        timeToMinutes(option.value)
      )})`,
    }));
  }, [startTime]);

  const handleStartTimeChange = (val: string | null) => {
    setStartTime(val);
    if (endTime && val) {
      const valMinutes = timeToMinutes(val);
      const endMinutes = timeToMinutes(endTime);

      // Reset endTime if it's invalid (same time or after startTime without wrap-around)
      // Allow wrap-around: if endTime is 00:00:00 and startTime is not 00:00:00, it's valid
      if (
        valMinutes === endMinutes ||
        (endMinutes !== 0 && valMinutes >= endMinutes)
      ) {
        setEndTime(null);
      }
    }
  };

  const handleEndTimeChange = (val: string | null) => {
    setEndTime(val);
    if (startTime && val) {
      const valMinutes = timeToMinutes(val);
      const startMinutes = timeToMinutes(startTime);

      // Reset startTime if it's invalid
      // Allow wrap-around: if endTime is 00:00:00 and startTime is not 00:00:00, it's valid
      if (
        valMinutes === startMinutes ||
        (valMinutes !== 0 && valMinutes <= startMinutes)
      ) {
        setStartTime(null);
      }
    }
  };

  return (
    <Flex gap={2}>
      <Box width="100%">
        <CustomSelect
          disableSortOptions
          readOnly={fullDay}
          label="Start Time"
          value={startTime || ""}
          onChange={handleStartTimeChange}
          options={filteredStartTimeOptions}
          error={startTimeError}
          required={required}
        />
      </Box>
      <Box width="100%">
        <CustomSelect
          disableSortOptions
          required={required}
          readOnly={fullDay}
          label="End Time"
          value={endTime || ""}
          onChange={handleEndTimeChange}
          options={filteredEndTimeOptions}
          error={endTimeError}
        />
      </Box>
    </Flex>
  );
}

export default TimeSlotDropdown;
